<template>
  <div>
    <TransactionalPageBreadcrumbs
      class="my-4"
      :location-name="locationSlugDisplayName"
      :location-slug="locationSlug"
      :category-slug="categSlug"
      :tag-slug="tagSlug"
      :general-slug="generalSlug"
      :type="pageType as EventListPageType"
      :event-list-page-obj="eventListPageData"
    />

    <div class="event-list-page__section mb-0">
      <div
        class="relative h-[80px] md:h-auto rounded-xl overflow-hidden md:overflow-visible flex items-center"
      >
        <NuxtImg
          preload
          class="card-image object-cover w-full md:hidden"
          src="/_nuxt3-static/general/transactional-pages-banner.webp"
          alt="Ein Barkeeper gießt Cocktails in zwei Gläser."
          loading="eager"
          fetchpriority="high"
        />

        <div
          class="w-full h-full bg-blend-hard-light bg-gradient-to-r from-white to-transparent absolute"
        ></div>

        <Heading
          v-if="searchHeading"
          class="absolute md:relative flex md:justify-start justify-center items-center w-full h-full pt-2"
          h-class="!text-teal-900 !text-xl font-semibold"
          :title="searchHeading"
          :level="1"
        >
        </Heading>
      </div>
    </div>
    <FilteredProductShowcase
      :page-type="pageType as EventListPageType"
      :event-list-page-data="eventListPageData"
      :load-schema="true"
      :reviews-data="reviewsData"
    />

    <ReviewsTransactional
      :reviews="rawReviews"
      :title="eventListPageData?.title"
    />

    <!-- Same idea as EventDetails: padding + horizontal clip on one box so Swiper -mx-4 bleeds to column
         edges without widening the document. Scoped here so .filters-bar (above) is not under overflow-x. -->
    <div
      class="-mx-4 px-4 overflow-x-hidden md:mx-0 md:px-0 md:overflow-x-visible"
    >
      <NuxtLazyHydrate when-visible>
        <ProductBoxShowcase
          v-if="['category', 'city-category'].includes(pageType)"
          :title="
            t('eventListPage.kitsTitle', {
              category: capitalizedCategorySlug,
            })
          "
          title-class="text-h3"
          :category="categSlug"
          :is-duration-visible="false"
          is-mobile-slider
        />
      </NuxtLazyHydrate>
    </div>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCityCategoriesList
        v-if="EventListPageType.CITY === pageType"
        :subtitle="eventListPageData?.title"
        :location-slug="locationSlug"
        :category-slug="categSlug"
        class="pt-10"
        :section-title="
          t('eventListPage.city.cityCategoryHorizontalListTitle', {
            city: locationSlugDisplayName,
          })
        "
        data-test-id="city-category-horizontal-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCityCategoriesList
        v-if="EventListPageType.CATEGORY === pageType"
        :location-slug="undefined"
        :category-slug="categSlug"
        class="pt-10"
        :section-title="
          t('eventListPage.category.categoryCitiesHorizontalListTitle', {
            category: capitalizedCategorySlug,
          })
        "
        data-test-id="category-cities-horizontal-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCardListFromStrapi
        v-if="shouldShowHorizontalCardListFromStrapi"
        :event-list-page="eventListPageData"
        class="pt-10"
        data-test-id="horizontal-card-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <vue-markdown
        class="prose my-2"
        :source="eventListPageAttributes?.description || ''"
      ></vue-markdown>
    </NuxtLazyHydrate>

    <div class="mt-16">
      <Heading
        :level="4"
        :title="strapiEventListPageGetters.getShowcaseTitle(eventListPageData)"
        h-class="text-h3 text-stone-800"
        class="kft-heading--left"
      />
      <NuxtLazyHydrate when-visible>
        <SimpleCardShowcase
          class="mt-4"
          :showcase-items="cardShowcase"
          data-test-id="simple-showcase"
        />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render'
import * as qs from 'qs'
import type { Ref } from 'vue'
import {
  EventListPageType,
  type EventListPageTypeString,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import { useResolvedEventListPage } from '~/composables/transactionalPage/useResolvedEventListPage'
import { useEventListPageSlugs } from '~/composables/transactionalPage/useEventListPageSlugs'
import { useTransactionalPageReviews } from '~/composables/transactionalPage/useTransactionalPageReviews'
import { eventListPageLocalitiesUrl } from '~/components/transactionalPage/requestParams'

const props = defineProps<{
  pageType: EventListPageTypeString
}>()

const { t, locale } = useI18n()

const {
  locationSlug,
  categorySlug: categSlug,
  generalSlug,
  tagSlug,
} = useEventListPageSlugs(props.pageType)

const { data: _eventListPageData } = await useResolvedEventListPage({
  pageType: props.pageType,
  slug: generalSlug,
  locationSlug,
  categorySlug: categSlug,
})
if (!_eventListPageData.value) {
  throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const eventListPageData = _eventListPageData as Ref<StrapiEventListPage>

const eventListPageAttributes = computed(() => {
  return eventListPageData?.value
})

const shouldShowHorizontalCardListFromStrapi = computed(() => {
  return (
    strapiEventListPageGetters.getHorizontalCardList(eventListPageData.value)
      .length > 0 && props.pageType !== EventListPageType.CATEGORY
  )
})

const cardShowcase = computed(() => {
  return Object.keys(eventListPageAttributes.value?.showcase || {})
    ?.map((key) => eventListPageAttributes.value?.showcase[key])
    .filter((cardDetails) => cardDetails?.id)
    .map((cd) => ({
      ...cd,
      imageUrl: strapiImageGetters.getImageUrl(cd.image),
    }))
})

const searchHeading = computed(
  () =>
    strapiEventListPageGetters.getSearchHeading(eventListPageData.value) ||
    strapiEventListPageGetters.getTitle(eventListPageData.value)
)

const shouldFetchLocalities = [
  EventListPageType.CITY_CATEGORY,
  EventListPageType.CITY,
].includes(props.pageType as EventListPageType)

// Needed in:
// 1. city page for the horizontal city-category list
// 2. city-category page for the breadcrumbs
const { data: localityResponse } =
  (shouldFetchLocalities &&
    (await useKftApiFetch<KftApiDefaultResponse<{ name: string }>>(
      eventListPageLocalitiesUrl({ locationSlug })
    ))) ||
  {}

const locationSlugLocalityName = localityResponse?.value?.data.name

// For region/state/neighborhood location types, the KFT locality API returns no data.
// Fall back to fetching the location page title from Strapi.
const shouldFetchLocationPage =
  shouldFetchLocalities && !locationSlugLocalityName && !!locationSlug

const { data: locationPageFromStrapi } =
  (shouldFetchLocationPage &&
    (await useStrapiSingleType(
      `event-list-pages?${qs.stringify(
        {
          locale: locale.value,
          fields: ['title'],
          filters: {
            citySlug: { $eq: locationSlug },
            categorySlug: { $null: true },
          },
        },
        { encode: false }
      )}`,
      {
        transform: (response: any) => response?.data?.[0] ?? null,
      }
    ))) ||
  {}

const locationSlugDisplayName =
  locationSlugLocalityName ?? locationPageFromStrapi?.value?.title

const capitalizedCategorySlug = computed(() => {
  if (!categSlug) return ''

  if (categSlug.toLowerCase() === 'diy') return 'DIY'

  return categSlug.charAt(0).toUpperCase() + categSlug.slice(1)
})

// Load reviews data for schema before rendering components
const getReviewsSlug = computed(() => {
  // Composite slugs (e.g. "nordrhein-westfalen--r") indicate region/state/neighborhood
  // pages whose reviews are not supported by the API.
  if (locationSlug?.includes('--')) return ''

  const citySlugValue = eventListPageData.value?.citySlug
  const categorySlugValue = eventListPageData.value?.categorySlug

  if (citySlugValue && categorySlugValue) {
    return citySlugValue + '-' + categorySlugValue
  }

  if (citySlugValue) {
    return citySlugValue
  }

  return categorySlugValue || ''
})

const { reviews: rawReviews } = await useTransactionalPageReviews(
  props.pageType,
  getReviewsSlug.value,
  locale.value
)

// Transform reviews data for schema
const reviewsData = computed(() => {
  if (!rawReviews.value || rawReviews.value.length === 0) return null

  const transformedReviews = rawReviews.value.map((review: any) => ({
    ...review,
    created_at: review.review_date,
    id: review.review_id,
    rating: parseFloat(review.event_avg_review),
    customer: review.reviewee_name,
    review: review.review_text,
    reviewee_name: review.reviewee_name,
    review_text: review.review_text,
    review_date: review.review_date,
  }))

  const sum = rawReviews.value.reduce(
    (acc: number, review: any) =>
      acc + parseFloat(review?.event_avg_review || '5'),
    0
  )
  const average = sum / rawReviews.value.length
  const averageRating = new Intl.NumberFormat(locale.value, {
    maximumSignificantDigits: 2,
    minimumSignificantDigits: 2,
  }).format(average)

  return {
    reviews: transformedReviews,
    averageRating,
    reviewCount: rawReviews.value.length,
  }
})

const {
  set: setGlobalEventListPage,
  clearIfOwner: clearGlobalEventListPageIfOwner,
} = useEventListPageStore()

watch(eventListPageData, (newValue) => setGlobalEventListPage(newValue), {
  immediate: true,
})

onBeforeUnmount(() => {
  clearGlobalEventListPageIfOwner(eventListPageData.value)
})
</script>

<style lang="postcss">
.prose {
  max-width: 100%;
  width: 100%;
  table {
    width: unset;
  }
}
</style>
